import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "scanner-container" }
const _hoisted_2 = { class: "header" }
const _hoisted_3 = { class: "viewport" }
const _hoisted_4 = {
  key: 0,
  class: "detection-indicator"
}
const _hoisted_5 = {
  key: 1,
  class: "processing-overlay"
}
const _hoisted_6 = { class: "controls" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 0,
  class: "result-section"
}
const _hoisted_10 = { class: "image-viewer" }
const _hoisted_11 = ["src"]
const _hoisted_12 = { class: "viewer-controls" }
const _hoisted_13 = { class: "debug-panel" }
const _hoisted_14 = { class: "console-content" }
const _hoisted_15 = { class: "log-time" }
const _hoisted_16 = { class: "log-message" }

interface LogEntry {
  time: string
  message: string
  type: 'info' | 'success' | 'error'
}

import {
  ref,
  onUnmounted,
  watch,
  nextTick,
} from 'vue';
import { Scanner } from 'scanic';
import type { CornerPoints } from 'scanic';

const __default__ = { name: 'ScannerView' };

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const videoRef = ref<HTMLVideoElement | null>(null);
const cameraActive = ref(false);
const documentDetected = ref(false);
const isProcessing = ref(false);
const processedDocument = ref<string | null>(null);
const isZoomed = ref(false);
const debugCanvas = ref<HTMLCanvasElement | null>(null);
const showDebugCorners = ref(false);
const detectedDebugCorners = ref<any>(null);
// Статус для отображения пользователю
const statusText = ref('📷 Нажмите "Запустить камеру"');
const statusClass = ref('status-idle');

// Логи для отладки (только важное)
const logs = ref<LogEntry[]>([]);

// Сканер
let scanner: Scanner | null = null;
let mediaStream: MediaStream | null = null;
let detectionInterval: number | null = null;

// ==================== Вспомогательные функции ====================
const addLog = (message: string, type: LogEntry['type'] = 'info') => {
  const now = new Date();
  const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;

  logs.value.unshift({ time, message, type });

  // Ограничиваем количество логов
  if (logs.value.length > 50) {
    logs.value.pop();
  }

  console.log(`[${time}] ${message}`);
};

const updateStatus = (text: string, type: string) => {
  statusText.value = text;
  statusClass.value = type;
};

// ==================== Работа с камерой ====================
const startCamera = async () => {
  addLog('Запрос доступа к камере...', 'info');
  updateStatus('🎥 Запрос доступа...', 'status-warning');

  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        facingMode: 'environment',
      },
    });

    if (videoRef.value) {
      videoRef.value.srcObject = mediaStream;
      videoRef.value.onloadedmetadata = () => {
        videoRef.value?.play();
        cameraActive.value = true;
        updateStatus('✅ Камера готова', 'status-success');
        addLog(`Камера запущена: ${videoRef.value?.videoWidth}x${videoRef.value?.videoHeight}`, 'success');

        // Инициализируем сканер и начинаем поиск
        initScannerAndStartDetection();
      };
    }
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : 'Неизвестная ошибка';
    addLog(`Ошибка камеры: ${errMsg}`, 'error');
    updateStatus('❌ Ошибка доступа к камере', 'status-error');
  }
};

// ==================== Инициализация сканера ====================
const initScannerAndStartDetection = async () => {
  addLog('Инициализация сканера...', 'info');
  updateStatus('🔄 Загрузка сканера...', 'status-warning');

  try {
    scanner = new Scanner();
    await scanner.initialize();
    addLog('✅ Сканер готов', 'success');
    updateStatus('🔍 Ищу документ...', 'status-warning');

    // Запускаем цикл детекции
    startDetectionLoop();
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : 'Неизвестная ошибка';
    addLog(`Ошибка инициализации: ${errMsg}`, 'error');
    updateStatus('❌ Ошибка сканера', 'status-error');
  }
};

// ==================== Захват кадра ====================
const captureCurrentFrame = (): HTMLCanvasElement | null => {
  const video = videoRef.value;
  if (!video || !cameraActive.value || video.readyState !== 4) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
};

// ==================== Обнаружение с отладкой ====================
const detectDocument = async () => {
  if (!scanner || !cameraActive.value) return;

  const canvas = captureHighQualityFrame();
  if (!canvas) return;

  try {
    const result = await scanner.scan(canvas, {
      mode: 'detect',
      maxProcessingDimension: 800,
      lowThreshold: 40, // Более чувствительно
      highThreshold: 120,
      minArea: 10000, // Увеличил минимальную площадь
      debug: true,
    });

    if (result.success && result.corners) {
      if (!documentDetected.value) {
        addLog(`✅ Документ найден! Углы: TL(${Math.round(result.corners.topLeft.x)},${Math.round(result.corners.topLeft.y)})`, 'success');
        updateStatus('✅ Документ найден! Нажмите "Сделать снимок"', 'status-success');
      }
      documentDetected.value = true;

      // Сохраняем углы для отладки
      if (showDebugCorners.value) {
        detectedDebugCorners.value = result.corners;
        drawDebugCorners(canvas, result.corners);
      }
    } else {
      if (documentDetected.value) {
        addLog('Документ пропал из кадра', 'info');
        updateStatus('🔍 Ищу документ...', 'status-warning');
      }
      documentDetected.value = false;
      detectedDebugCorners.value = null;
      if (showDebugCorners.value) {
        clearDebugCanvas();
      }
    }
  } catch (error) {
    documentDetected.value = false;
  }
};

// Отрисовка углов на отладочном canvas
const drawDebugCorners = (sourceCanvas: HTMLCanvasElement, corners: any) => {
  if (!debugCanvas.value || !videoRef.value) return;

  const video = videoRef.value;
  const debugCtx = debugCanvas.value.getContext('2d');
  if (!debugCtx) return;

  // Настраиваем размер отладочного canvas
  debugCanvas.value.width = video.clientWidth;
  debugCanvas.value.height = video.clientHeight;
  debugCanvas.value.style.width = `${video.clientWidth}px`;
  debugCanvas.value.style.height = `${video.clientHeight}px`;

  // Очищаем
  debugCtx.clearRect(0, 0, debugCanvas.value.width, debugCanvas.value.height);

  // Коэффициенты пересчета
  const scaleX = debugCanvas.value.width / sourceCanvas.width;
  const scaleY = debugCanvas.value.height / sourceCanvas.height;

  const points = [
    corners.topLeft, corners.topRight,
    corners.bottomRight, corners.bottomLeft,
  ];

  // Рисуем линии
  debugCtx.beginPath();
  debugCtx.strokeStyle = '#00ff00';
  debugCtx.lineWidth = 3;

  const scaledPoints = points.map((p) => ({
    x: p.x * scaleX,
    y: p.y * scaleY,
  }));

  debugCtx.moveTo(scaledPoints[0].x, scaledPoints[0].y);
  debugCtx.lineTo(scaledPoints[1].x, scaledPoints[1].y);
  debugCtx.lineTo(scaledPoints[2].x, scaledPoints[2].y);
  debugCtx.lineTo(scaledPoints[3].x, scaledPoints[3].y);
  debugCtx.closePath();
  debugCtx.stroke();

  // Рисуем точки
  scaledPoints.forEach((point) => {
    debugCtx.fillStyle = '#ff0000';
    debugCtx.beginPath();
    debugCtx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
    debugCtx.fill();
  });
};

const clearDebugCanvas = () => {
  if (!debugCanvas.value) return;
  const ctx = debugCanvas.value.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, debugCanvas.value.width, debugCanvas.value.height);
  }
};

// ==================== Цикл детекции ====================
const startDetectionLoop = () => {
  if (detectionInterval) clearInterval(detectionInterval);
  detectionInterval = window.setInterval(() => {
    detectDocument();
  }, 200); // 5 раз в секунду
};

const stopDetectionLoop = () => {
  if (detectionInterval) {
    clearInterval(detectionInterval);
    detectionInterval = null;
  }
};
// ==================== Захват кадра в высоком качестве ====================
const captureHighQualityFrame = (): HTMLCanvasElement | null => {
  const video = videoRef.value;
  if (!video || !cameraActive.value || video.readyState !== 4) {
    return null;
  }

  const canvas = document.createElement('canvas');
  // Используем НАТИВНОЕ разрешение видео (максимальное качество)
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  addLog(`Захвачен кадр: ${canvas.width}x${canvas.height}`, 'info');
  return canvas;
};

// ==================== ОСНОВНОЕ: Снимок с расширением границ ====================
const captureAndProcess = async () => {
  if (!documentDetected.value || !scanner) {
    addLog('Нет документа для сканирования', 'error');
    return;
  }

  isProcessing.value = true;
  updateStatus('🔄 Обработка документа...', 'status-warning');
  addLog('📸 Делаю снимок в максимальном качестве...', 'info');

  stopDetectionLoop();

  // Захватываем кадр в ОРИГИНАЛЬНОМ разрешении камеры
  const canvas = captureHighQualityFrame();
  if (!canvas) {
    addLog('Не удалось захватить кадр', 'error');
    isProcessing.value = false;
    startDetectionLoop();
    return;
  }

  addLog(`Обработка изображения ${canvas.width}x${canvas.height}...`, 'info');

  try {
    // Сначала находим углы с более чувствительными настройками
    const detectResult = await scanner.scan(canvas, {
      mode: 'detect',
      maxProcessingDimension: 1200,
      lowThreshold: 30, // Более низкий порог для захвата слабых границ
      highThreshold: 100,
      minArea: 5000, // Меньшая минимальная площадь
      dilationKernelSize: 5, // Увеличиваем ядро для связывания границ
      debug: true,
    });

    if (!detectResult.success || !detectResult.corners) {
      addLog('Не удалось точно определить границы документа', 'error');
      updateStatus('❌ Ошибка определения границ', 'status-error');
      isProcessing.value = false;
      startDetectionLoop();
      return;
    }

    // Получаем оригинальные углы
    const originalCorners = detectResult.corners;
    addLog(`Оригинальные границы: TL(${Math.round(originalCorners.topLeft.x)},${Math.round(originalCorners.topLeft.y)})`, 'success');

    // РАСШИРЯЕМ ГРАНИЦЫ наружу на 5-10%
    const expandedCorners = expandCorners(
      originalCorners,
      canvas.width,
      canvas.height,
      0.08,
    ); // 8% отступ

    addLog(`Расширенные границы: TL(${Math.round(expandedCorners.topLeft.x)},${Math.round(expandedCorners.topLeft.y)})`, 'success');

    // Создаем временный canvas с расширенными границами
    const expandedCanvas = cropAndExpandCanvas(canvas, originalCorners, expandedCorners);

    // Теперь извлекаем документ с расширенными границами
    const extractResult = await scanner.scan(expandedCanvas, {
      mode: 'extract',
      output: 'dataurl',
      maxProcessingDimension: 2000,
      lowThreshold: 30,
      highThreshold: 100,
      minArea: 5000,
    });

    if (extractResult.success && extractResult.output) {
      processedDocument.value = extractResult.output as string;
      addLog('✅ Документ успешно отсканирован с расширенными границами!', 'success');
      updateStatus('✅ Готово!', 'status-success');
      stopCamera();
    } else {
      // Fallback: пробуем без расширения
      addLog('Пробую без расширения границ...', 'info');
      const fallbackResult = await scanner.scan(canvas, {
        mode: 'extract',
        output: 'dataurl',
        maxProcessingDimension: 2000,
        lowThreshold: 30,
        highThreshold: 100,
        minArea: 5000,
      });

      if (fallbackResult.success && fallbackResult.output) {
        processedDocument.value = fallbackResult.output as string;
        addLog('✅ Документ отсканирован (стандартный режим)', 'success');
        updateStatus('✅ Готово!', 'status-success');
        stopCamera();
      } else {
        addLog('❌ Ошибка извлечения', 'error');
        updateStatus('❌ Ошибка, попробуйте снова', 'status-error');
        startDetectionLoop();
      }
    }
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : 'Неизвестная ошибка';
    addLog(`Ошибка обработки: ${errMsg}`, 'error');
    updateStatus('❌ Ошибка обработки', 'status-error');
    startDetectionLoop();
  } finally {
    isProcessing.value = false;
  }
};

// ==================== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ДЛЯ РАСШИРЕНИЯ ГРАНИЦ ====================

// Расширяет границы наружу на заданный процент
const expandCorners = (
  corners: any,
  imageWidth: number,
  imageHeight: number,
  paddingPercent: number,
) => {
  // Находим центр документа
  const centerX = (
    corners.topLeft.x + corners.topRight.x + corners.bottomRight.x + corners.bottomLeft.x) / 4;
  const centerY = (
    corners.topLeft.y + corners.topRight.y + corners.bottomRight.y + corners.bottomLeft.y) / 4;

  // Функция для расширения точки относительно центра
  const expandPoint = (point: { x: number, y: number }, factor: number) => ({
    x: centerX + (point.x - centerX) * factor,
    y: centerY + (point.y - centerY) * factor,
  });

  // Расширяем все углы на (1 + paddingPercent)
  const factor = 1 + paddingPercent;
  const expanded = {
    topLeft: expandPoint(corners.topLeft, factor),
    topRight: expandPoint(corners.topRight, factor),
    bottomRight: expandPoint(corners.bottomRight, factor),
    bottomLeft: expandPoint(corners.bottomLeft, factor),
  };

  // Ограничиваем границами изображения
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

  expanded.topLeft.x = clamp(expanded.topLeft.x, 0, imageWidth);
  expanded.topLeft.y = clamp(expanded.topLeft.y, 0, imageHeight);
  expanded.topRight.x = clamp(expanded.topRight.x, 0, imageWidth);
  expanded.topRight.y = clamp(expanded.topRight.y, 0, imageHeight);
  expanded.bottomRight.x = clamp(expanded.bottomRight.x, 0, imageWidth);
  expanded.bottomRight.y = clamp(expanded.bottomRight.y, 0, imageHeight);
  expanded.bottomLeft.x = clamp(expanded.bottomLeft.x, 0, imageWidth);
  expanded.bottomLeft.y = clamp(expanded.bottomLeft.y, 0, imageHeight);

  return expanded;
};

// Создает новый canvas с расширенной областью документа
const cropAndExpandCanvas = (
  sourceCanvas: HTMLCanvasElement,
  originalCorners: any,
  expandedCorners: any,
) => {
  // Находим bounding box расширенных углов
  const xs = [
    expandedCorners.topLeft.x, expandedCorners.topRight.x,
    expandedCorners.bottomRight.x, expandedCorners.bottomLeft.x,
  ];
  const ys = [
    expandedCorners.topLeft.y, expandedCorners.topRight.y,
    expandedCorners.bottomRight.y, expandedCorners.bottomLeft.y,
  ];

  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  const width = maxX - minX;
  const height = maxY - minY;

  // Создаем новый canvas
  const newCanvas = document.createElement('canvas');
  newCanvas.width = width;
  newCanvas.height = height;

  const ctx = newCanvas.getContext('2d');
  if (!ctx) return sourceCanvas;

  // Копируем область с расширенными границами
  ctx.drawImage(
    sourceCanvas,
    minX,
    minY,
    width,
    height,
    0,
    0,
    width,
    height,
  );

  addLog(`Расширенная область: ${width}x${height} (было ${sourceCanvas.width}x${sourceCanvas.height})`, 'info');

  return newCanvas;
};

// ==================== Остановка камеры ====================
const stopCamera = () => {
  stopDetectionLoop();
  if (mediaStream) {
    mediaStream.getTracks().forEach((track) => track.stop());
    mediaStream = null;
  }
  if (videoRef.value) {
    videoRef.value.srcObject = null;
  }
  cameraActive.value = false;
  documentDetected.value = false;
};

// ==================== Работа с результатом ====================
const toggleZoom = () => {
  isZoomed.value = !isZoomed.value;
};

const downloadDocument = () => {
  if (!processedDocument.value) return;

  const link = document.createElement('a');
  link.download = `scanned-document-${Date.now()}.png`;
  link.href = processedDocument.value;
  link.click();
  addLog('Файл скачан', 'success');
};

const clearResult = () => {
  processedDocument.value = null;
  isZoomed.value = false;
  addLog('Результат очищен', 'info');
  updateStatus('🔍 Ищу документ...', 'status-warning');

  // Перезапускаем камеру и детекцию
  startCamera();
};

// ==================== Очистка при размонтировании ====================
onUnmounted(() => {
  stopDetectionLoop();
  if (mediaStream) {
    mediaStream.getTracks().forEach((track) => track.stop());
  }
  addLog('Компонент размонтирован', 'info');
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        class: _normalizeClass(["status-badge", statusClass.value])
      }, _toDisplayString(statusText.value), 3)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("video", {
        ref_key: "videoRef",
        ref: videoRef,
        autoplay: "",
        playsinline: "",
        muted: "",
        class: "camera-preview"
      }, null, 512),
      (documentDetected.value && !isProcessing.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[0] || (_cache[0] = [
            _createElementVNode("div", { class: "indicator-icon" }, "✅", -1),
            _createElementVNode("div", { class: "indicator-text" }, "Документ обнаружен", -1)
          ]))]))
        : _createCommentVNode("", true),
      (isProcessing.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[1] || (_cache[1] = [
            _createElementVNode("div", { class: "spinner" }, null, -1),
            _createElementVNode("div", { class: "processing-text" }, "Обработка документа...", -1)
          ]))]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("button", {
        onClick: startCamera,
        disabled: cameraActive.value,
        class: "btn btn-primary"
      }, " 📷 Запустить камеру ", 8, _hoisted_7),
      _createElementVNode("button", {
        onClick: captureAndProcess,
        disabled: !documentDetected.value || isProcessing.value,
        class: "btn btn-success"
      }, " 📸 Сделать снимок ", 8, _hoisted_8)
    ]),
    (processedDocument.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _cache[2] || (_cache[2] = _createElementVNode("h3", null, "📄 Отсканированный документ", -1)),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", {
              class: _normalizeClass(["image-container", { 'zoomed': isZoomed.value }])
            }, [
              _createElementVNode("img", {
                src: processedDocument.value,
                alt: "Processed document",
                onClick: toggleZoom,
                class: "result-image"
              }, null, 8, _hoisted_11)
            ], 2),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("button", {
                onClick: toggleZoom,
                class: "btn btn-small"
              }, _toDisplayString(isZoomed.value ? '🔍 Уменьшить' : '🔍 Увеличить'), 1),
              _createElementVNode("button", {
                onClick: downloadDocument,
                class: "btn btn-small btn-primary"
              }, " 💾 Скачать "),
              _createElementVNode("button", {
                onClick: clearResult,
                class: "btn btn-small btn-secondary"
              }, " 🗑️ Очистить ")
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("details", _hoisted_13, [
      _createElementVNode("summary", null, "📋 Технические логи (" + _toDisplayString(logs.value.length) + ")", 1),
      _createElementVNode("div", _hoisted_14, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(logs.value.slice(0, 20), (log, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: _normalizeClass(['log-entry', log.type])
          }, [
            _createElementVNode("span", _hoisted_15, _toDisplayString(log.time), 1),
            _createElementVNode("span", _hoisted_16, _toDisplayString(log.message), 1)
          ], 2))
        }), 128))
      ])
    ])
  ]))
}
}

})